/**
 * App FORMS manager (ADR-0048 single-IDE) — a builder rail BOTTOM-zone area where
 * the operator AUTHORS the app's forms (field picker + sections + test submit)
 * WITHOUT leaving the editor. Part of the north-star "app-detail goes away;
 * everything lives in the editor".
 *
 * Follows the WS-7/WS-8 rail-bottom MASTER/DETAIL pattern (mirror of
 * `AppDatasetsPane`): a `view` prop (`"list"|"detail"|"full"`) with CONTROLLED
 * selection lifted to `PageBuilderPage`, mounted TWICE — the LIST in the lateral
 * sidebar, the DETAIL in the content. The DETAIL reuses the SHARED `FormBuilder`
 * (extracted from `FormBuilderPage`) — the same body the standalone route renders.
 * Like datasets, a form needs a BASE TABLE chosen at creation, so "＋ Nuovo form"
 * opens a small dialog (name + slug + table) and selects the new form.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, Plus, Trash2 } from "lucide-react";
import * as React from "react";

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api";
import { appsApi, type FormCreate, type FormOut, type TableSummary } from "@/lib/api-apps";
import { slugify } from "@/pages/AppsPage";

import { FormBuilder } from "@/pages/FormBuilderPage";

const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : String(e));

type AreaView = "list" | "detail" | "full";

export function AppFormsPane({
  appId,
  canWrite = true,
  view = "full",
  selectedId: selectedIdProp,
  onSelectId,
}: {
  appId: string;
  canWrite?: boolean;
  view?: AreaView;
  /** Controlled selection (master/detail split). A form id or null. */
  selectedId?: string | null;
  onSelectId?: (id: string | null) => void;
}) {
  const qc = useQueryClient();
  const formsQ = useQuery({
    queryKey: ["apps", appId, "forms"],
    queryFn: () => appsApi.listForms(appId),
  });
  const forms = React.useMemo(() => formsQ.data ?? [], [formsQ.data]);

  const tablesQ = useQuery({
    queryKey: ["apps", appId, "tables"],
    queryFn: () => appsApi.listTables(appId),
  });
  const tables = React.useMemo(() => tablesQ.data ?? [], [tablesQ.data]);
  const tableName = (id: string) => tables.find((tb) => tb.id === id)?.name ?? id;

  const controlled = onSelectId !== undefined;
  const [internalId, setInternalId] = React.useState<string | null>(null);
  const selectedId = controlled ? selectedIdProp ?? null : internalId;
  const setSelectedId = (id: string | null) => (controlled ? onSelectId!(id) : setInternalId(id));

  const [error, setError] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState("");
  const [createOpen, setCreateOpen] = React.useState(false);

  const visible = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return forms;
    return forms.filter((x) => x.name.toLowerCase().includes(q));
  }, [forms, search]);

  const deleteMut = useMutation({
    mutationFn: (id: string) => appsApi.deleteForm(appId, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["apps", appId, "forms"] });
      setSelectedId(null);
    },
    onError: (e) => setError(errMsg(e)),
  });

  const selected = selectedId ? forms.find((f) => f.id === selectedId) ?? null : null;

  // --- MASTER (list) ---------------------------------------------------------
  const masterCls =
    view === "full"
      ? "flex w-56 shrink-0 flex-col border-r border-border/60 pr-2"
      : "flex h-full min-h-0 flex-col";

  const master = (
    <div className={masterCls}>
      <div className="mb-2 shrink-0 px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Form
      </div>
      {canWrite && (
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          disabled={tables.length === 0}
          title={tables.length === 0 ? "Crea prima una tabella" : undefined}
          className="mb-1 flex shrink-0 items-center justify-center gap-1.5 rounded-md border border-dashed border-border px-2 py-1.5 text-xs text-muted-foreground hover:border-accent/60 hover:text-foreground disabled:opacity-40"
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={1.5} /> Nuovo form
        </button>
      )}
      {forms.length > 5 && (
        <input
          className="mb-1 rounded border border-border bg-background px-1.5 py-1 text-[11px]"
          placeholder="Cerca…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      )}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {forms.length === 0 ? (
          <p className="px-1 py-2 text-[11px] text-muted-foreground">
            {tables.length === 0
              ? "Aggiungi prima una tabella, poi crea un form."
              : "Nessun form. Creane uno con «Nuovo»."}
          </p>
        ) : visible.length === 0 ? (
          <p className="px-1 py-2 text-[11px] text-muted-foreground">Nessun risultato.</p>
        ) : (
          visible.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setSelectedId(f.id)}
              className={`mb-0.5 flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-left text-[12px] ${
                selectedId === f.id ? "bg-accent/15 text-accent" : "hover:bg-surface"
              }`}
            >
              <FileText
                className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70"
                strokeWidth={1.75}
              />
              <span className="min-w-0 flex-1 truncate font-medium">{f.name}</span>
              <span className="shrink-0 truncate text-[9px] text-muted-foreground">
                {tableName(f.table_id)}
              </span>
            </button>
          ))
        )}
      </div>
      {error && (
        <pre className="mt-1 shrink-0 whitespace-pre-wrap break-words rounded border border-danger/40 bg-danger/5 p-1.5 font-mono text-[10px] text-danger">
          {error}
        </pre>
      )}
    </div>
  );

  // --- DETAIL ----------------------------------------------------------------
  const detailInner = !selected ? (
    <div className="flex flex-1 items-center justify-center text-[12px] text-muted-foreground">
      Seleziona un form dalla lista, o creane uno nuovo.
    </div>
  ) : (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mb-2 flex shrink-0 items-center gap-2 border-b border-border/50 pb-2">
        <FileText className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.5} />
        <span className="min-w-0 flex-1 truncate text-sm font-semibold">{selected.name}</span>
        {canWrite && (
          <button
            type="button"
            onClick={() => {
              if (window.confirm(`Eliminare il form "${selected.name}"?`)) deleteMut.mutate(selected.id);
            }}
            className="flex items-center gap-1 rounded px-2 py-1 text-[11px] text-danger hover:bg-danger/10"
          >
            <Trash2 className="h-3 w-3" strokeWidth={1.5} /> Elimina
          </button>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <FormBuilder appId={appId} formId={selected.id} canWrite={canWrite} />
      </div>
    </div>
  );

  const createDialog = (
    <CreateFormDialog
      appId={appId}
      tables={tables}
      open={createOpen}
      onOpenChange={setCreateOpen}
      onCreated={(f) => setSelectedId(f.id)}
    />
  );

  if (view === "list")
    return (
      <>
        {master}
        {createDialog}
      </>
    );
  if (view === "detail")
    return <div className="flex h-full min-h-0 flex-col text-[12px]">{detailInner}</div>;

  return (
    <div className="flex h-full min-h-0 gap-3 text-[12px]">
      {master}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">{detailInner}</div>
      {createDialog}
    </div>
  );
}

/** Create dialog — a form needs a base table (immutable later). Selects the new
 *  form on success (no navigation — we live inside the editor). */
function CreateFormDialog({
  appId,
  tables,
  open,
  onOpenChange,
  onCreated,
}: {
  appId: string;
  tables: TableSummary[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (f: FormOut) => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = React.useState("");
  const [slug, setSlug] = React.useState("");
  const [slugTouched, setSlugTouched] = React.useState(false);
  const [tableId, setTableId] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setName("");
      setSlug("");
      setSlugTouched(false);
      setTableId(tables[0]?.id ?? "");
    }
  }, [open, tables]);

  const effectiveSlug = slugTouched ? slug : slugify(name);

  const create = useMutation({
    mutationFn: (body: FormCreate) => appsApi.createForm(appId, body),
    onSuccess: (form) => {
      qc.invalidateQueries({ queryKey: ["apps", appId, "forms"] });
      onOpenChange(false);
      onCreated(form);
    },
  });

  const canSubmit =
    name.trim().length > 0 && effectiveSlug.length > 0 && tableId.length > 0 && !create.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nuovo form</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="form-name">Form</Label>
            <Input
              id="form-name"
              value={name}
              autoFocus
              placeholder="Nuovo ticket"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="form-slug">Slug</Label>
            <Input
              id="form-slug"
              value={effectiveSlug}
              className="font-mono"
              placeholder="nuovo_ticket"
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(slugify(e.target.value));
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="form-table">Tabella</Label>
            <select
              id="form-table"
              value={tableId}
              className="block w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm"
              onChange={(e) => setTableId(e.target.value)}
            >
              {tables.map((tb) => (
                <option key={tb.id} value={tb.id}>
                  {tb.name}
                </option>
              ))}
            </select>
          </div>
          {create.isError && (
            <Alert tone="danger" title="Impossibile creare il form">
              <span className="font-mono text-xs">{(create.error as Error).message}</span>
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button
            disabled={!canSubmit}
            onClick={() => create.mutate({ name: name.trim(), slug: effectiveSlug, table_id: tableId })}
          >
            Crea
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
